import { useEffect, useState } from "react";
import { X, ExternalLink } from "lucide-react";
import { FRANCIS_CONNECT_URL } from "@/data/links";

const AVATAR = "/francisconnect-avatar.jpg";
const SEEN_KEY = "fc-greeting-seen";

const Avatar = ({ size = "w-16 h-16" }) => (
  <span className={`relative block ${size} rounded-full overflow-hidden ring-2 ring-gold/80 ring-offset-2 ring-offset-parchment-100 shadow-[0_12px_30px_-10px_rgba(60,40,20,0.6)]`}>
    <img src={AVATAR} alt="Saint Francis — FrancisConnect" className="w-full h-full object-cover scale-110" />
  </span>
);

function Greeting({ onOpen, onDismiss }) {
  return (
    <div data-testid="fc-greeting" className="absolute bottom-[78px] right-0 w-64 bg-parchment-50 border border-parchment-400 shadow-[0_18px_40px_-20px_rgba(60,40,20,0.5)] p-4 animate-fade-up">
      <button onClick={onDismiss} aria-label="Dismiss" data-testid="fc-greeting-dismiss" className="absolute top-2 right-2 text-cocoa-700/60 hover:text-terracotta">
        <X size={14} />
      </button>
      <div className="font-serif italic text-cocoa-900 text-[15px]">Pax et Bonum!</div>
      <p className="mt-1 text-[13px] text-cocoa-700 font-sans leading-snug pr-3">
        Questions about Masses, sacraments or your GKK? I'm happy to help.
      </p>
      <button onClick={onOpen} className="mt-3 text-[10px] uppercase tracking-[0.22em] text-terracotta hover:text-cocoa-900 font-sans">
        Ask FrancisConnect →
      </button>
      <span className="absolute -bottom-[7px] right-7 w-3 h-3 bg-parchment-50 border-r border-b border-parchment-400 rotate-45" />
    </div>
  );
}

function ChatPanel({ onClose }) {
  return (
    <div
      data-testid="fc-panel"
      className="fixed z-[60] inset-0 sm:inset-auto sm:bottom-6 sm:right-6 sm:w-[400px] sm:h-[min(640px,calc(100vh-48px))] bg-parchment-50 sm:border border-parchment-400 shadow-[0_30px_70px_-25px_rgba(40,25,15,0.6)] flex flex-col animate-fade-up"
    >
      <div className="flex items-center gap-3 px-4 py-3 bg-cocoa-900 text-parchment-100">
        <Avatar size="w-10 h-10" />
        <div className="flex-1 leading-tight">
          <div className="font-serif text-lg">FrancisConnect</div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-gold">Parish AI Assistant</div>
        </div>
        <a href={FRANCIS_CONNECT_URL} target="_blank" rel="noopener noreferrer" aria-label="Open in new tab" data-testid="fc-open-full" className="p-2 text-parchment-100/80 hover:text-gold">
          <ExternalLink size={16} />
        </a>
        <button onClick={onClose} aria-label="Close" data-testid="fc-close" className="p-2 text-parchment-100/80 hover:text-gold">
          <X size={18} />
        </button>
      </div>
      <iframe title="FrancisConnect parish assistant" src={FRANCIS_CONNECT_URL} className="flex-1 w-full border-0 bg-white" allow="clipboard-write; microphone" />
    </div>
  );
}

export default function FrancisConnectWidget() {
  const [open, setOpen] = useState(false);
  const [greet, setGreet] = useState(false);
  const [hover, setHover] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(SEEN_KEY)) return;
    const show = setTimeout(() => {
      setGreet(true);
      sessionStorage.setItem(SEEN_KEY, "1");
    }, 9000);
    const hide = setTimeout(() => setGreet(false), 21000);
    return () => { clearTimeout(show); clearTimeout(hide); };
  }, []);

  const openChat = () => { setGreet(false); setOpen(true); };

  if (open) return <ChatPanel onClose={() => setOpen(false)} />;

  return (
    <div className="fixed bottom-6 right-6 z-50" data-testid="fc-widget">
      {greet && <Greeting onOpen={openChat} onDismiss={() => setGreet(false)} />}
      <button
        onClick={openChat}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        data-testid="fc-launcher"
        aria-label="Chat with FrancisConnect, the parish assistant"
        className="group flex items-center gap-3"
      >
        <span
          className={`hidden sm:block bg-cocoa-900 text-parchment-100 text-[11px] uppercase tracking-[0.2em] font-sans px-4 py-2 rounded-full shadow-lg transition-[opacity,transform] duration-300 ${
            hover ? "opacity-100 translate-x-0" : "opacity-0 translate-x-2 pointer-events-none"
          }`}
        >
          Ask FrancisConnect
        </span>
        <span className="relative">
          <span className="absolute inset-0 rounded-full bg-gold/40 animate-[ping_3s_ease-out_2] pointer-events-none" />
          <Avatar />
          <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-sage-dark border-2 border-parchment-100" />
        </span>
      </button>
    </div>
  );
}
